/**
 * Тонкая обёртка над `window.Telegram.WebApp`.
 *
 * Namespace стабилен и документирован, в отличие от версий SDK-обёрток,
 * поэтому работаем с ним напрямую. `initData` уходит на бэкенд как есть —
 * там он проверяется по HMAC-SHA256 с токеном бота.
 */

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

interface MainButton {
  text: string;
  isVisible: boolean;
  setText: (text: string) => void;
  setParams: (params: { text?: string; color?: string; is_active?: boolean }) => void;
  show: () => void;
  hide: () => void;
  showProgress: (leaveActive?: boolean) => void;
  hideProgress: () => void;
  onClick: (cb: () => void) => void;
  offClick: (cb: () => void) => void;
}

interface BackButton {
  isVisible: boolean;
  show: () => void;
  hide: () => void;
  onClick: (cb: () => void) => void;
  offClick: (cb: () => void) => void;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: TelegramUser; start_param?: string };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  ready: () => void;
  expand: () => void;
  close: () => void;
  openLink: (url: string) => void;
  MainButton: MainButton;
  BackButton: BackButton;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/** true, когда приложение открыто внутри Telegram, а не в обычном браузере. */
export function isInsideTelegram(): boolean {
  const wa = getWebApp();
  return Boolean(wa && wa.initData.length > 0);
}

export function initTelegram(): TelegramWebApp | null {
  const wa = getWebApp();
  if (!wa) return null;

  wa.ready();
  wa.expand();
  return wa;
}

export function getTelegramUser(): TelegramUser | null {
  return getWebApp()?.initDataUnsafe.user ?? null;
}

/** Сырая строка initData — по ней бэкенд опознаёт пользователя. */
export function getInitDataRaw(): string {
  return getWebApp()?.initData ?? '';
}

/**
 * Внешняя ссылка. Платёжные страницы Click и Payme не работают внутри WebView
 * Telegram, поэтому открываем их системным браузером; вне Telegram — обычной
 * вкладкой, иначе оплата из отладочного браузера была бы недоступна.
 */
export function openLink(url: string): void {
  const wa = getWebApp();
  if (wa) wa.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

/** Deep-link вида `?startapp=product_<id>` — открывает товар сразу из бота. */
export function getStartParam(): string | null {
  return getWebApp()?.initDataUnsafe.start_param ?? null;
}

export const haptic = {
  tap: () => getWebApp()?.HapticFeedback?.impactOccurred('light'),
  select: () => getWebApp()?.HapticFeedback?.selectionChanged(),
  success: () => getWebApp()?.HapticFeedback?.notificationOccurred('success'),
  error: () => getWebApp()?.HapticFeedback?.notificationOccurred('error'),
};

/** Нижняя кнопка Telegram: нативный аналог основной кнопки экрана. */
export function setMainButton(
  options: { text: string; onClick: () => void; loading?: boolean; disabled?: boolean } | null,
): () => void {
  const wa = getWebApp();
  if (!wa) return () => undefined;

  if (!options) {
    wa.MainButton.hide();
    return () => undefined;
  }

  wa.MainButton.setParams({ text: options.text, is_active: !options.disabled });
  wa.MainButton.show();

  if (options.loading) wa.MainButton.showProgress(true);
  else wa.MainButton.hideProgress();

  wa.MainButton.onClick(options.onClick);

  return () => {
    wa.MainButton.offClick(options.onClick);
    wa.MainButton.hide();
  };
}

export function setBackButton(onClick: (() => void) | null): () => void {
  const wa = getWebApp();
  if (!wa) return () => undefined;

  if (!onClick) {
    wa.BackButton.hide();
    return () => undefined;
  }

  wa.BackButton.show();
  wa.BackButton.onClick(onClick);

  return () => {
    wa.BackButton.offClick(onClick);
    wa.BackButton.hide();
  };
}
